import { DateTime } from 'luxon'

export const DAILY_HOME_PATH = '/overview'
export const DAILY_HOME_DESTINATION_KEY = 'dailyHomeDestination'
export const DAILY_HOME_AUTOMATIC_REDIRECT_STATE = { preserveDailyHomeDestination: true } as const

type DailyHomeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

interface DailyHomeDestination {
    localDate: string
    path: string
}

const localDate = () => DateTime.local().toISODate()

// Only shell-level destinations are resumable. Detail routes and routes outside MainScreen must
// remain explicit links instead of quietly becoming tomorrow's home.
export const isMainViewDestination = (path: string) =>
    /^\/tasks(?:\/[^/?#]+)?\/?$/.test(path) || ['/notes', '/recurring-tasks', '/routines', '/goals'].includes(path)

export const clearDailyHomeDestination = (storage: DailyHomeStorage = window.localStorage) => {
    try {
        storage.removeItem(DAILY_HOME_DESTINATION_KEY)
    } catch {
        // A blocked or full local store should degrade to Daily Overview, never block navigation.
    }
}

export const rememberDailyHomeDestination = (
    path: string,
    storage: DailyHomeStorage = window.localStorage,
    today: string = localDate()
) => {
    if (path === DAILY_HOME_PATH) {
        clearDailyHomeDestination(storage)
        return
    }
    if (!isMainViewDestination(path)) return

    const destination: DailyHomeDestination = { localDate: today, path }
    try {
        storage.setItem(DAILY_HOME_DESTINATION_KEY, JSON.stringify(destination))
    } catch {
        // Navigation remains usable when storage is unavailable.
    }
}

export const getDailyHomeDestination = (
    storage: DailyHomeStorage = window.localStorage,
    today: string = localDate()
) => {
    let value: unknown
    try {
        const stored = storage.getItem(DAILY_HOME_DESTINATION_KEY)
        if (!stored) return DAILY_HOME_PATH
        value = JSON.parse(stored)
    } catch {
        clearDailyHomeDestination(storage)
        return DAILY_HOME_PATH
    }

    if (
        !value ||
        typeof value !== 'object' ||
        Array.isArray(value) ||
        (value as DailyHomeDestination).localDate !== today ||
        typeof (value as DailyHomeDestination).path !== 'string' ||
        !isMainViewDestination((value as DailyHomeDestination).path)
    ) {
        clearDailyHomeDestination(storage)
        return DAILY_HOME_PATH
    }

    return (value as DailyHomeDestination).path
}

export const isDailyHomeAutomaticRedirect = (state: unknown) =>
    Boolean(
        state &&
            typeof state === 'object' &&
            (state as { preserveDailyHomeDestination?: unknown }).preserveDailyHomeDestination === true
    )
